// La Terra: texture NASA (Blue Marble di giorno, Black Marble di notte), terminatore morbido calcolato dalla vera
// posizione del Sole, riflesso del Sole sugli oceani, nuvole e atmosfera. Raggio = 1 unità (= 6.371 km).
// Il gruppo ruota con il tempo siderale (GMST): così le coordinate dei satelliti (inerziali) restano allineate.

import * as THREE from 'three';

export class Terra {
  readonly gruppo = new THREE.Group();
  private readonly materiale: THREE.ShaderMaterial;
  private readonly nuvole: THREE.Mesh;
  private readonly atmosfera: THREE.ShaderMaterial;
  readonly direzioneSole = new THREE.Vector3(1, 0, 0);
  /** Strato dei paesi (Potenze spaziali): colore per indice di paese, 256 x 1. */
  readonly coloriPaesi = new THREE.DataTexture(new Uint8Array(256 * 4), 256, 1, THREE.RGBAFormat);

  constructor(caricatore: THREE.TextureLoader, anisotropia: number, leggera: boolean) {
    const res = leggera ? 2048 : 4096;
    const carica = (url: string, srgb = true) => {
      const t = caricatore.load(url);
      t.anisotropy = anisotropia;
      if (srgb) t.colorSpace = THREE.SRGBColorSpace;
      return t;
    };

    this.materiale = new THREE.ShaderMaterial({
      uniforms: {
        uGiorno: { value: carica(`/texture/terra-giorno-${res}.webp`) },
        uNotte: { value: carica(`/texture/terra-notte-${res}.webp`) },
        uSole: { value: this.direzioneSole },
        uPaesiId: { value: null },
        uPaesiColori: { value: this.coloriPaesi },
        uPaesi: { value: 0 },
        uPaesiScelto: { value: -1 },
      },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        varying vec3 vNormale;
        varying vec3 vPosMondo;
        void main() {
          vUv = uv;
          vNormale = normalize(mat3(modelMatrix) * normal);
          vec4 pm = modelMatrix * vec4(position, 1.0);
          vPosMondo = pm.xyz;
          gl_Position = projectionMatrix * viewMatrix * pm;
        }`,
      fragmentShader: /* glsl */ `
        uniform sampler2D uGiorno;
        uniform sampler2D uNotte;
        uniform vec3 uSole;
        uniform sampler2D uPaesiId;
        uniform sampler2D uPaesiColori;
        uniform float uPaesi;
        uniform float uPaesiScelto;
        varying vec2 vUv;
        varying vec3 vNormale;
        varying vec3 vPosMondo;
        void main() {
          vec3 n = normalize(vNormale);
          vec3 v = normalize(cameraPosition - vPosMondo);
          float cosSole = dot(n, uSole);
          // Terminatore morbido (crepuscolo ~ 6°) e luce diffusa.
          float giorno = smoothstep(-0.12, 0.18, cosSole);
          vec3 albedo = texture2D(uGiorno, vUv).rgb;
          vec3 luci = texture2D(uNotte, vUv).rgb;
          vec3 diffusa = albedo * (max(cosSole, 0.0) * 1.35 + 0.015);
          // Oceano: riconosciuto dal colore (blu dominante e scuro), riceve il riflesso del Sole.
          float acqua = smoothstep(0.02, 0.10, albedo.b - max(albedo.r, albedo.g)) * (1.0 - smoothstep(0.25, 0.45, albedo.g));
          vec3 h = normalize(uSole + v);
          float spec = pow(max(dot(n, h), 0.0), 60.0) * acqua * giorno;
          // Città di notte, un po' più calde e solo dove è buio.
          vec3 notte = pow(luci, vec3(1.6)) * vec3(1.35, 1.05, 0.7) * 1.9 * (1.0 - giorno);
          // Arrossamento al crepuscolo.
          float crepuscolo = (1.0 - abs(cosSole * 4.0 - 0.3)) * 0.5;
          vec3 col = diffusa + notte + vec3(1.0, 0.92, 0.8) * spec * 0.9;
          col += vec3(0.9, 0.35, 0.12) * max(crepuscolo, 0.0) * 0.05;
          // Bordo atmosferico (fresnel) sul lato illuminato.
          float fres = pow(1.0 - max(dot(n, v), 0.0), 3.0);
          col += vec3(0.30, 0.55, 1.0) * fres * (0.15 + 0.85 * giorno) * 0.55;
          if (uPaesi > 0.001) {
            // Paesi: colore della categoria (si vede anche di notte), confini sottili, paese scelto più chiaro.
            float id = floor(texture2D(uPaesiId, vUv).r * 255.0 + 0.5);
            vec4 c = texture2D(uPaesiColori, vec2((id + 0.5) / 256.0, 0.5));
            // I colori arrivano in sRGB: si portano in lineare come il resto della scena.
            col = mix(col, pow(c.rgb, vec3(2.2)) * (0.55 + 0.75 * giorno), c.a * uPaesi);
            float bordo = clamp(fwidth(id) * 4.0, 0.0, 1.0) * step(0.5, id + fwidth(id));
            col = mix(col, vec3(0.92), bordo * 0.28 * uPaesi);
            if (abs(id - uPaesiScelto) < 0.5) col = mix(col, vec3(1.0), 0.22 * uPaesi);
          }
          gl_FragColor = vec4(col, 1.0);
        }`,
    });
    const segmenti = leggera ? 96 : 160;
    const sfera = new THREE.Mesh(new THREE.SphereGeometry(1, segmenti, segmenti / 2), this.materiale);
    this.gruppo.add(sfera);

    // Le nuvole sono morbide: 1024 px bastano (384 → 105 kB).
    const texNuvole = carica('/texture/terra-nuvole-1024.webp', false);
    this.nuvole = new THREE.Mesh(
      new THREE.SphereGeometry(1.006, segmenti, segmenti / 2),
      new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        uniforms: { uNuvole: { value: texNuvole }, uSole: { value: this.direzioneSole } },
        vertexShader: /* glsl */ `
          varying vec2 vUv; varying vec3 vNormale;
          void main() { vUv = uv; vNormale = normalize(mat3(modelMatrix) * normal);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
        fragmentShader: /* glsl */ `
          uniform sampler2D uNuvole; uniform vec3 uSole;
          varying vec2 vUv; varying vec3 vNormale;
          void main() {
            // I grigi più tenui (residui del suolo nella texture) non diventano velature.
            float a = smoothstep(0.1, 0.85, texture2D(uNuvole, vUv).r);
            float luce = smoothstep(-0.1, 0.25, dot(normalize(vNormale), uSole));
            gl_FragColor = vec4(vec3(0.95) * (0.03 + luce * 0.97), a * 0.85 * (0.15 + luce * 0.85));
          }`,
      }),
    );
    this.gruppo.add(this.nuvole);

    this.atmosfera = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
      uniforms: { uSole: { value: this.direzioneSole } },
      vertexShader: /* glsl */ `
        varying vec3 vNormale; varying vec3 vPosMondo;
        void main() { vNormale = normalize(mat3(modelMatrix) * normal);
          vec4 pm = modelMatrix * vec4(position, 1.0); vPosMondo = pm.xyz;
          gl_Position = projectionMatrix * viewMatrix * pm; }`,
      fragmentShader: /* glsl */ `
        uniform vec3 uSole; varying vec3 vNormale; varying vec3 vPosMondo;
        void main() {
          vec3 v = normalize(cameraPosition - vPosMondo);
          float bordo = pow(clamp(1.0 + dot(normalize(vNormale), v) * 1.0, 0.0, 1.0), 0.0);
          float alone = pow(max(dot(-normalize(vNormale), v), 0.0), 5.0);
          float luce = smoothstep(-0.35, 0.4, dot(normalize(vNormale), uSole));
          vec3 col = mix(vec3(1.0, 0.45, 0.2), vec3(0.32, 0.6, 1.0), luce);
          gl_FragColor = vec4(col * alone * (0.08 + luce * 1.1) * bordo, 1.0);
        }`,
    });
    const atm = new THREE.Mesh(new THREE.SphereGeometry(1.06, 96, 48), this.atmosfera);
    atm.renderOrder = 2;
    // L'atmosfera non ruota con la Terra (è simmetrica): resta nel gruppo per semplicità.
    this.gruppo.add(atm);
  }

  /** Carica la mappa d'identità dei paesi (solo quando serve). */
  caricaPaesi(caricatore: THREE.TextureLoader): void {
    const u = this.materiale.uniforms;
    if (u.uPaesiId.value) return;
    const t = caricatore.load('/texture/paesi-id.png');
    t.generateMipmaps = false;
    t.minFilter = THREE.NearestFilter;
    t.magFilter = THREE.NearestFilter;
    t.colorSpace = THREE.NoColorSpace;
    u.uPaesiId.value = t;
  }

  /** Intensità dello strato dei paesi (0 = spento, 1 = pieno) e paese evidenziato (indice, -1 = nessuno). */
  impostaPaesi(intensita: number, scelto: number): void {
    this.materiale.uniforms.uPaesi.value = intensita;
    this.materiale.uniforms.uPaesiScelto.value = scelto;
  }

  /** gmst in radianti; sole = direzione del Sole in coordinate della scena. */
  aggiorna(gmst: number, sole: THREE.Vector3): void {
    this.gruppo.rotation.y = gmst;
    this.direzioneSole.copy(sole).normalize();
    // Le nuvole restano ferme sul suolo: la loro texture contiene deboli contorni dei continenti, che facendole
    // scorrere (a ×600 un giro ogni 6 s) diventavano un «fantasma» staccato dai continenti veri.
  }
}
